import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SignaturePad } from 'angular2-signaturepad';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';

import { AppUtilService } from 'app/shared/service/app-util.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { SitemateApiService } from 'app/sitemate-api/sitemate-api.service';
import { UserService } from 'app/user/user.service';

import { SignUpService } from '../../sign-up.service';

@Component({
  selector: 'cc-sign-up-user-signature',
  templateUrl: './sign-up-user-signature.component.html',
  styleUrls: ['./sign-up-user-signature.component.scss', '../../sign-up.component.scss'],
})
export class SignUpUserSignatureComponent implements OnInit {
  @ViewChild('userSignatureForm')
  userSignatureForm: NgForm;

  imageSignature = false;
  signatureInProgress = false;
  isProcessing = false;

  model = { signature: '' };

  signatureUploading: boolean;
  imageSignatureUrl: string;
  signaturePad: SignaturePad;
  signaturePadOptions: Record<string, any> = {
    canvasWidth: 310,
    canvasHeight: 96,
  };
  unSubscribe = new Subject<void>();

  constructor(
    readonly signupService: SignUpService,
    private readonly appUtilService: AppUtilService,
    private readonly sitemateApi: SitemateApiService,
    public readonly activeModal: NgbActiveModal,
    private readonly errorHandler: ErrorHandler,
    private readonly userService: UserService,
  ) {}

  ngOnInit(): void {
    this.setSignaturePadOptions();
    this.subscribeToSignupProcessing();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  subscribeToSignupProcessing() {
    this.signupService.isProcessing$.pipe(takeUntil(this.unSubscribe)).subscribe((isProcessing) => {
      this.isProcessing = isProcessing;
    });
  }

  setSignaturePadOptions(): void {
    const { isSmallWindow } = this.appUtilService;
    if (isSmallWindow) {
      this.signaturePadOptions = {
        canvasWidth: 260,
        canvasHeight: 70,
      };
    }
  }

  async onFinalSubmit(form: NgForm) {
    if (form?.valid && this.model.signature) {
      try {
        this.signupService.setProcessing(true);
        const signatureFile = this.appUtilService.dataUrlToBlob(this.model.signature);
        const { url: signature } = await lastValueFrom(this.sitemateApi.uploadSignature(signatureFile));
        await lastValueFrom(this.userService.updateCurrentUser());
        this.activeModal.close({ signature });
      } catch (error) {
        this.signupService.setProcessing(false);
        this.errorHandler.handle(error);
      }
    }
  }

  hasFormError(field: NgModel): boolean {
    const currentForm = this.userSignatureForm;
    const requiredValidation = currentForm?.submitted && field.untouched && !field.dirty;
    const hasError = (field.invalid && (field.dirty || field.touched)) || requiredValidation;

    return hasError;
  }

  hasSignatureError(signature: NgModel): boolean {
    const requiredValidation = this.userSignatureForm?.submitted && signature.untouched;
    return (signature.invalid || signature.touched) && requiredValidation;
  }

  uploadSignature(drawEvent: any): void {
    const fileList = drawEvent.target.files;
    if (fileList.length > 0) {
      this.signatureUploading = true;
      const file = fileList[0];
      const reader = new FileReader();
      reader.onload = (event: any) => {
        this.imageSignatureUrl = event.target.result;
        this.model.signature = String(reader.result);
        this.imageSignature = true;
      };
      reader.readAsDataURL(file);
      this.signatureUploading = false;
    }
  }

  clearSignature(): void {
    if (this.signaturePad) {
      this.signatureInProgress = false;
      this.signaturePad.clear();
    }

    this.imageSignature = false;
    this.model.signature = '';
  }

  drawStart(): void {
    this.signatureInProgress = true;
  }

  drawComplete(event: SignaturePad): void {
    this.signaturePad = event;
    this.model.signature = this.signaturePad.toDataURL();
  }
}
